// ============================================================
// src/pages/Calendar.jsx
//
// Monthly calendar grid with gig badges and trip shading.
//
// Trips are simplified: each trip has a check-in date
// (arrive_dest_at) and a heading-home date (depart_dest_at).
// The calendar shades days between those dates and marks the
// check-in / check-out days distinctly via CSS modifier classes.
//
// Removed from earlier versions:
//   - transport_mode / TRANSPORT_ICONS / TRANSPORT_LABELS
//   - one_way trip type and one_way_arrive kind
//   - depart_home_at / arrive_home_at logic
//   - terminal buffer labels on calendar cells
//   - getDepartDayTrip helper
//   - TravelDayModal (replaced by simpler TripDayModal)
//
// CSS class inventory (all defined in index.css):
//   .calendar-page         page container
//   .cal-trip-section      trip banners + add button wrapper
//   .trip-banner           individual trip banner row
//   .trip-banner--active   active trip tint
//   .trip-banner__*        BEM children (body, name-row, meta, etc.)
//   .trip-cal-dropdown     export menu
//   .trip-cal-dropdown-btn export menu button
//   .cal-add-trip-btn      dashed add-trip button
//   .calendar-container    grid card wrapper
//   .calendar-header       month nav row
//   .calendar-grid         7-column CSS grid
//   .calendar-day-header   "Sun Mon..." labels
//   .calendar-day          individual day cell
//   .calendar-day.empty    filler cells before day 1
//   .calendar-day.has-gigs pink cell tint
//   .calendar-day.today    yellow highlight
//   .calendar-day.trip-day suitcase icon host
//   .calendar-day--trip-depart  check-in day modifier
//   .calendar-day--trip-stay    mid-trip day modifier
//   .calendar-day--trip-return  check-out day modifier
//   .cal-day--clickable    cursor: pointer
//   .cal-trip-icon-row     icon centering wrapper inside cells
//   .cal-trip-cell-icon    base icon class
//   .cal-trip-cell-icon--primary   check-in/out icon style
//   .cal-trip-cell-icon--secondary stay-day icon style
//   .day-number            date numeral inside cell
//   .gig-indicators        badge list inside cell
//   .gig-indicator         individual pink gig badge
//   .cal-legend            legend row below grid
//   .cal-legend-item       legend key+label pair
//   .cal-legend-swatch     coloured square swatch
//   .cal-legend-swatch--gig       pink swatch
//   .cal-legend-swatch--today     yellow swatch
//   .cal-legend-swatch-icon       icon variant of swatch
//   .cal-legend-swatch-icon--stay      stay day icon
//   .cal-legend-swatch-icon--travel    travel day icon
//   .cal-modal-overlay     full-screen modal backdrop
//   .cal-modal-inner       modal content wrapper
//   .cal-modal-header      date label + close button row
//   .cal-modal-date-label  formatted date heading
//   .cal-modal-close       close button
//   .travel-day-card       dark gradient trip summary card
//   .travel-day-card--depart / --return / --stay  modifiers
//   .travel-day-card--mb   margin-bottom when gigs follow
//   .travel-day-card__*    BEM children
//   .travel-day-gigs-label section label above gig previews
// ============================================================

import { useState, useEffect } from 'react'
import { supabase }            from '../lib/supabaseClient'
import { useGigs }             from '../hooks/useGigs'
import { useTripMode }         from '../hooks/useTripMode'
import { useMusicTracks }      from '../hooks/useMusicTracks'
import { useNotificationPrefs }         from '../hooks/useNotificationPrefs'
import { useScheduleTripNotifications } from '../hooks/useScheduleTripNotifications'
import AddTripModal    from '../components/AddTripModal'
import { GigDayPreviews } from '../components/GigDayPreview'
import { Link } from 'react-router-dom'


// ── CONSTANTS ────────────────────────────────────────────────

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
]

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']


// ── HELPERS ───────────────────────────────────────────────────

// Format the date portion of an ISO string as "Jun 1"
function fmtShortDate(iso) {
  if (!iso) return ''
  const [y, m, d] = iso.split('T')[0].split('-').map(Number)
  return new Date(y, m - 1, d).toLocaleDateString('en-US', {
    month: 'short',
    day:   'numeric',
  })
}

// Build a local YYYY-MM-DD string from a year/month(0-based)/day triple
// without risking UTC offset shifting the date.
function toDateStr(year, month, day) {
  return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`
}

// Build today's local date string once at module level so it never
// re-evaluates during renders.
function getTodayStr() {
  const now = new Date()
  return toDateStr(now.getFullYear(), now.getMonth(), now.getDate())
}


// ── DELETE CONFIRM MODAL ──────────────────────────────────────
// Replaces window.confirm so the destructive action uses a DPP-styled
// modal instead of the browser's native (un-styled) dialog.
//
// Props:
//   tripLabel (string)    destination name shown in the body copy
//   onConfirm (function)  called when user confirms deletion
//   onCancel  (function)  called when user cancels
function DeleteConfirmModal({ tripLabel, onConfirm, onCancel }) {
  return (
    <div
      className="app-modal-overlay"
      onClick={e => { if (e.target === e.currentTarget) onCancel() }}
    >
      <div
        className="trip-modal trip-modal--narrow"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="delete-modal-title"
      >
        {/* Header */}
        <div className="app-modal-header">
          <div className="app-modal-header__left">
            <i
              className="bi bi-trash3-fill cal-delete-icon"
              aria-hidden="true"
            />
            <span
              id="delete-modal-title"
              className="app-modal-header__title"
            >
              Remove Trip
            </span>
          </div>
          <button className="app-modal-header__close" onClick={onCancel} aria-label="Cancel">
            <i className="bi bi-x-lg" aria-hidden="true" />
          </button>
        </div>

        {/* Body */}
        <div className="app-modal-body">
          <p>
            Remove <strong>{tripLabel || 'this trip'}</strong>? This cannot be undone.
            Scheduled notifications for this trip will also be cleared.
          </p>
        </div>

        {/* Footer */}
        <div className="app-modal-footer">
          <button className="modal-btn modal-btn--cancel" onClick={onCancel}>
            Keep Trip
          </button>
          <button
            className="modal-btn cal-remove-btn"
            onClick={onConfirm}
          >
            <i className="bi bi-trash3-fill cal-icon-gap" aria-hidden="true" />
            Remove
          </button>
        </div>
      </div>
    </div>
  )
}


// ── DAY GIG MODAL ─────────────────────────────────────────────
// Opens when a date cell that has gigs but no trip info is tapped.
function DayGigModal({
  dateStr,
  gigs,
  homeLat,
  homeLng,
  homeAddress,
  prepMinutes,
  musicTracks,
  onClose,
}) {
  if (!dateStr || !gigs || gigs.length === 0) return null

  const [y, mo, d] = dateStr.split('-').map(Number)
  const dateLabel  = new Date(y, mo - 1, d).toLocaleDateString('en-US', {
    weekday: 'long', month: 'long', day: 'numeric',
  })

  return (
    <div
      className="cal-modal-overlay"
      onClick={e => { if (e.target === e.currentTarget) onClose() }}
      role="dialog"
      aria-modal="true"
      aria-label={dateLabel}
    >
      <div className="cal-modal-inner">
        <div className="cal-modal-header">
          <div className="cal-modal-date-label">{dateLabel}</div>
          <button className="cal-modal-close" onClick={onClose} aria-label="Close">
            <i className="bi bi-x-lg" aria-hidden="true" />
          </button>
        </div>
        <GigDayPreviews
          gigs={gigs}
          dateStr={dateStr}
          homeLat={homeLat}
          homeLng={homeLng}
          homeAddress={homeAddress}
          prepMinutes={prepMinutes}
          musicTracks={musicTracks}
        />
      </div>
    </div>
  )
}


// ── ICS / GOOGLE CALENDAR HELPERS ────────────────────────────

// Download an ICS file for a trip (Apple Calendar, Outlook)
function buildTripICS(trip) {
  const label   = (trip.destination_label || 'Trip').trim()
  const pad     = n => String(n).padStart(2, '0')

  const toICS = (iso) => {
    if (!iso) return null
    const d = iso.split('T')[0].split('-')
    return `${d[0]}${d[1]}${d[2]}`
  }

  // ICS DTEND for all-day events is exclusive (one day after last day)
  const addOneDay = (dateStr) => {
    if (!dateStr) return dateStr
    const [y, m, d] = dateStr.split('-').map(Number)
    const next = new Date(y, m - 1, d + 1)
    return `${next.getFullYear()}${pad(next.getMonth() + 1)}${pad(next.getDate())}`
  }

  const dtStart = toICS(trip.arrive_dest_at)
  const dtEnd   = addOneDay(trip.depart_dest_at?.split('T')[0])
  if (!dtStart) return

  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Drag Pocket Planner//Trip//EN',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    'BEGIN:VEVENT',
    `UID:${trip.id}-trip@dragpocketplanner`,
    `SUMMARY:${label}`,
    `DTSTART;VALUE=DATE:${dtStart}`,
    `DTEND;VALUE=DATE:${dtEnd || dtStart}`,
    trip.stay_address ? `LOCATION:${trip.stay_address}` : '',
    trip.stay_address ? `DESCRIPTION:Staying at: ${trip.stay_address}` : '',
    'STATUS:CONFIRMED',
    'END:VEVENT',
    'END:VCALENDAR',
  ].filter(Boolean).join('\r\n')

  const blob = new Blob([lines], { type: 'text/calendar;charset=utf-8' })
  const url  = URL.createObjectURL(blob)
  const a    = document.createElement('a')
  a.href     = url
  a.download = `${label.replace(/\s+/g, '_')}_trip.ics`
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

// Open Google Calendar event creation page in a new tab
function openTripGoogleCalendar(trip) {
  const label  = (trip.destination_label || 'Trip').trim()
  const toGCal = (iso) => (iso ? iso.split('T')[0].replace(/-/g, '') : '')
  const params = new URLSearchParams({
    action:   'TEMPLATE',
    text:     label,
    dates:    `${toGCal(trip.arrive_dest_at)}/${toGCal(trip.depart_dest_at)}`,
    details:  trip.stay_address ? `Staying at: ${trip.stay_address}` : '',
    location: trip.stay_address || '',
  })
  window.open(
    `https://calendar.google.com/calendar/render?${params.toString()}`,
    '_blank',
    'noopener,noreferrer',
  )
}


// ── TRIP BANNER ───────────────────────────────────────────────
// Shown above the calendar grid for each trip visible this month.
function TripBanner({ trip, isActive, onEdit, onDelete }) {
  const [calMenuOpen, setCalMenuOpen] = useState(false)

  return (
    <div className={`trip-banner${isActive ? ' trip-banner--active' : ''}`}>

      {/* Suitcase icon — color differs between active and inactive */}
      <i
        className={`bi bi-suitcase-fill cal-trip-banner-icon${isActive ? ' cal-trip-banner-icon--active' : ''}`}
        aria-hidden="true"
      />

      <div className="trip-banner__body">

        <div className="trip-banner__name-row">
          <strong className="trip-banner__name">
            {trip.destination_label || 'Trip'}
          </strong>
          {isActive && (
            <span className="trip-banner__active-badge">ACTIVE</span>
          )}
        </div>

        {/* Check-in / check-out date summary */}
        <div className="trip-banner__meta">
          <i className="bi bi-box-arrow-right" aria-hidden="true" />
          {' '}Trip Start: {fmtShortDate(trip.arrive_dest_at)}
          {trip.depart_dest_at && (
            <>
              {' → '}
              <i className="bi bi-box-arrow-in-left" aria-hidden="true" />
              {' '}Trip End: {fmtShortDate(trip.depart_dest_at)}
            </>
          )}
        </div>

        {/* Stay address */}
        {trip.stay_address && (
          <div className="trip-banner__address">
            <i className="bi bi-house-fill" aria-hidden="true" /> {trip.stay_address}
          </div>
        )}
      </div>

      {/* Action buttons */}
      <div className="trip-banner__actions">

        {/* Calendar export dropdown */}
        <div className="cal-export-dropdown-wrap">
          <button
            className="trip-banner__btn trip-banner__btn--cal"
            onClick={() => setCalMenuOpen(prev => !prev)}
            aria-label="Add trip to calendar"
            aria-expanded={calMenuOpen}
          >
            <i className="bi bi-calendar-plus-fill" aria-hidden="true" />
          </button>

          {calMenuOpen && (
            <div className="trip-cal-dropdown">
              {[
                {
                  label:  'iCalendar (.ics)',
                  sub:    'Apple Calendar, Outlook',
                  icon:   'bi-calendar2-check',
                  action: () => { buildTripICS(trip); setCalMenuOpen(false) },
                },
                {
                  label:  'Google Calendar',
                  sub:    'Opens in new tab',
                  icon:   'bi-google',
                  action: () => { openTripGoogleCalendar(trip); setCalMenuOpen(false) },
                },
              ].map(item => (
                <button key={item.label} className="trip-cal-dropdown-btn" onClick={item.action}>
                  <i className={`bi ${item.icon}`} aria-hidden="true" />
                  <div>
                    <div>{item.label}</div>
                    <div className="trip-cal-dropdown-btn-sub">{item.sub}</div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        <button
          className="trip-banner__btn trip-banner__btn--edit"
          onClick={() => onEdit(trip)}
          aria-label={`Edit trip: ${trip.destination_label || 'Trip'}`}
        >
          <i className="bi bi-pencil-fill" aria-hidden="true" />
        </button>

        <button
          className="trip-banner__btn trip-banner__btn--del"
          onClick={() => onDelete(trip)}
          aria-label={`Delete trip: ${trip.destination_label || 'Trip'}`}
        >
          <i className="bi bi-trash3-fill" aria-hidden="true" />
        </button>
      </div>
    </div>
  )
}


// ── TRIP DAY MODAL ────────────────────────────────────────────
// Opens when the user taps a shaded trip day on the calendar.
// kind values: 'check_in' | 'stay' | 'check_out'
function TripDayModal({
  dateStr,
  travelInfo,
  gigs = [],
  homeLat,
  homeLng,
  homeAddress,
  prepMinutes,
  musicTracks,
  onClose,
}) {
  if (!dateStr || !travelInfo) return null

  const { trip, kind } = travelInfo

  const [y, mo, d] = dateStr.split('-').map(Number)
  const dateLabel  = new Date(y, mo - 1, d).toLocaleDateString('en-US', {
    weekday: 'long', month: 'long', day: 'numeric',
  })

  const headingText =
    kind === 'check_in'  ? `Checking in to ${trip.destination_label || 'Destination'}` :
    kind === 'check_out' ? `Heading home from ${trip.destination_label || 'Destination'}` :
    `On trip: ${trip.destination_label}`

  const badgeText =
    kind === 'check_in'  ? 'Check-in Day'  :
    kind === 'check_out' ? 'Check-out Day' :
    'On Trip'

  // BEM modifier drives the gradient variant in index.css
  const cardCls =
    kind === 'check_in'  ? 'travel-day-card--depart' :
    kind === 'check_out' ? 'travel-day-card--return'  :
    'travel-day-card--stay'

  return (
    <div
      className="cal-modal-overlay"
      onClick={e => { if (e.target === e.currentTarget) onClose() }}
      role="dialog"
      aria-modal="true"
      aria-label={`${headingText} — ${dateLabel}`}
    >
      <div className="cal-modal-inner">

        <div className="cal-modal-header">
          <div className="cal-modal-date-label">{dateLabel}</div>
          <button className="cal-modal-close" onClick={onClose} aria-label="Close">
            <i className="bi bi-x-lg" aria-hidden="true" />
          </button>
        </div>

        {/* Trip summary card */}
        <div className={`travel-day-card ${cardCls}${gigs.length > 0 ? ' travel-day-card--mb' : ''}`}>
          <div className="travel-day-card__orb" aria-hidden="true" />

          <div className="travel-day-card__badge">
            <i className="bi bi-suitcase-fill" aria-hidden="true" /> {badgeText}
          </div>

          {/* h3 because the modal itself is already a document landmark;
              h2 inside an aria modal would skip the h1 page heading */}
          <h3 className="travel-day-card__heading">{headingText}</h3>

          <p className="travel-day-card__sub">
            <i className="bi bi-suitcase-fill" aria-hidden="true" /> {trip.destination_label}
          </p>

          {trip.stay_address && (
            <div className="travel-day-card__stay">
              <i className="bi bi-house-fill travel-day-card__stay-icon" aria-hidden="true" />
              <div>
                <div className="travel-day-card__stay-label">Staying At</div>
                <div className="travel-day-card__stay-address">{trip.stay_address}</div>
              </div>
            </div>
          )}

          {gigs.length === 0 && (
            <p className="travel-day-card__no-gigs">
              <i className="bi bi-calendar3" aria-hidden="true" /> No gigs scheduled for this day.
            </p>
          )}
        </div>

        {/* Gig previews when gigs fall on this trip day */}
        {gigs.length > 0 && (
          <>
            <p className="travel-day-gigs-label">
              <i className="bi bi-star-fill" aria-hidden="true" />
              {' '}{gigs.length} Gig{gigs.length !== 1 ? 's' : ''} on This Day
            </p>
            <GigDayPreviews
              gigs={gigs}
              dateStr={dateStr}
              homeLat={homeLat}
              homeLng={homeLng}
              homeAddress={homeAddress}
              prepMinutes={prepMinutes}
              musicTracks={musicTracks}
            />
          </>
        )}
      </div>
    </div>
  )
}


// ── CALENDAR PAGE ─────────────────────────────────────────────
function Calendar() {

  const [currentDate,  setCurrentDate]  = useState(new Date())
  const [userId,       setUserId]       = useState(null)

  // Pending delete: holds the trip object so we can show its name
  // in the confirm modal. null = no modal.
  const [deletingTrip, setDeletingTrip] = useState(null)

  useEffect(() => {
    supabase.auth.getUser().then(({ data: { user } }) => {
      if (user) setUserId(user.id)
    })
  }, [])

  const { gigs, loading }                        = useGigs()
  const { trips, addTrip, updateTrip, deleteTrip,
          getActiveTripForDate }                  = useTripMode()
  const { scheduleForTrip, clearForTrip }         = useScheduleTripNotifications()
  const { tracks: musicTracks = [] }              = useMusicTracks()
  const {
    homeLat, homeLng, prepMinutes = 90,
    homeAddress, geocodedLabel,
  }                                               = useNotificationPrefs()

  const [modalOpen,       setModalOpen]       = useState(false)
  const [editingTrip,     setEditingTrip]     = useState(null)
  const [selectedDay,     setSelectedDay]     = useState(null)
  const [selectedTripDay, setSelectedTripDay] = useState(null)

  const todayStr = getTodayStr()


  // ── Calendar grid helpers ─────────────────────────────────

  const getDaysInMonth = () => {
    const year        = currentDate.getFullYear()
    const month       = currentDate.getMonth()
    const daysInMonth = new Date(year, month + 1, 0).getDate()
    const firstDay    = new Date(year, month, 1).getDay()
    const days        = []
    // Pad with null for empty cells before the first day
    for (let i = 0; i < firstDay; i++) days.push(null)
    for (let d = 1; d <= daysInMonth; d++) days.push(d)
    return days
  }

  const getGigsForDay = (day) => {
  if (!day) return []
  const target = toDateStr(currentDate.getFullYear(), currentDate.getMonth(), day)
  return gigs
    .filter(gig => {
      const gigDate = typeof gig.date === 'string'
        ? gig.date.split('T')[0]
        : new Date(gig.date).toISOString().split('T')[0]
      return gigDate === target
    })
    .sort((a, b) => (a.call_time || a.time || '').localeCompare(b.call_time || b.time || ''))
    // sort by call_time, falling back to show time, so earliest comes first --
    // same pattern used for todaysGigs/tomorrowsGigs on the dashboard
}

  const isToday = (day) => {
    if (!day) return false
    const today = new Date()
    return (
      day === today.getDate() &&
      currentDate.getMonth()    === today.getMonth() &&
      currentDate.getFullYear() === today.getFullYear()
    )
  }

  // Returns { trip, kind } where kind is 'check_in' | 'stay' | 'check_out',
  // or null if the day is not part of any trip.
  const getTravelDayInfo = (day) => {
    if (!day || !trips.length) return null

    const dateStr = toDateStr(currentDate.getFullYear(), currentDate.getMonth(), day)

    for (const trip of trips) {
      const checkIn  = trip.arrive_dest_at?.split('T')[0]
      const checkOut = trip.depart_dest_at?.split('T')[0]
      if (!checkIn || !checkOut) continue

      if (dateStr === checkIn)                     return { trip, kind: 'check_in'  }
      if (dateStr === checkOut)                    return { trip, kind: 'check_out' }
      if (dateStr > checkIn && dateStr < checkOut) return { trip, kind: 'stay'      }
    }

    return null
  }


  // ── Trips overlapping this calendar month ─────────────────
  const visibleTrips = trips.filter(trip => {
    if (!trip.arrive_dest_at || !trip.depart_dest_at) return false
    const year  = currentDate.getFullYear()
    const month = currentDate.getMonth()
    const daysInMonth = new Date(year, month + 1, 0).getDate()
    const monthStart = toDateStr(year, month, 1)
    const monthEnd   = toDateStr(year, month, daysInMonth)
    return (
      trip.arrive_dest_at.split('T')[0] <= monthEnd &&
      trip.depart_dest_at.split('T')[0] >= monthStart
    )
  })

  // Count gigs falling within the currently viewed month, for the
  // "X gigs this month" badge in the header.
  const gigsThisMonthCount = gigs.filter(gig => {
    const gigDateStr = typeof gig.date === 'string'
      ? gig.date.split('T')[0]
      : new Date(gig.date).toISOString().split('T')[0]
    const [gy, gm] = gigDateStr.split('-').map(Number)
    return gy === currentDate.getFullYear() && (gm - 1) === currentDate.getMonth()
  }).length


  // ── Event handlers ────────────────────────────────────────

  const openAddModal  = () => { setEditingTrip(null); setModalOpen(true) }
  const openEditModal = (trip) => { setEditingTrip(trip); setModalOpen(true) }

  const handleSaveTrip = async (formData) => {
    let savedTrip
    if (editingTrip) {
      await updateTrip(editingTrip.id, formData)
      savedTrip = { ...editingTrip, ...formData }
    } else {
      savedTrip = await addTrip(formData)
    }
    // Schedule push notifications for this trip's check-in / check-out days
    if (userId && savedTrip) await scheduleForTrip(savedTrip, userId)
  }

  // Receives the trip object (not just id) so we can show the name in the modal
  const handleDeleteRequest = (trip) => setDeletingTrip(trip)

  const handleDeleteConfirm = async () => {
    if (!deletingTrip) return
    if (userId) await clearForTrip(deletingTrip.id, userId)
    await deleteTrip(deletingTrip.id)
    setDeletingTrip(null)
  }

  const handleDayClick = (day) => {
    if (!day) return
    const dateStr    = toDateStr(currentDate.getFullYear(), currentDate.getMonth(), day)
    const dayGigs    = getGigsForDay(day)
    const travelInfo = getTravelDayInfo(day)

    // Do nothing if the cell has neither gigs nor trip info
    if (!dayGigs.length && !travelInfo) return

    if (travelInfo) {
      setSelectedTripDay({ dateStr, travelInfo, gigs: dayGigs })
    } else {
      setSelectedDay({ dateStr, gigs: dayGigs })
    }
  }

  const prevMonth = () =>
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1))
  const nextMonth = () =>
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1))


  // ── Loading state ─────────────────────────────────────────

  if (loading) {
    return (
      <div className="calendar-page">
        <h1 className="page-title">Calendar</h1>
        <p className="cal-loading-text">Loading your gigs...</p>
      </div>
    )
  }


  // ── Render ────────────────────────────────────────────────

  return (
    <div className="calendar-page">

      {/* Page header: title left, gig-count badge + quick actions right.
          Add Gig lives here now instead of the sidebar-only .add-gig-btn,
          since the sidebar is hidden on mobile (.sidebar { display:none }
          under 768px) and this page is reachable from any device. */}
      <div className="page-header-row">
        <h1 className="page-title">Calendar</h1>

        <div className="page-header-btn-group">
          <span className="cal-month-count-badge">
            {gigsThisMonthCount} gig{gigsThisMonthCount === 1 ? '' : 's'} this month
          </span>

          <button type="button" className="btn btn-outline-primary" onClick={openAddModal}>
            <i className="bi bi-suitcase-fill" aria-hidden="true" /> Add Trip
          </button>

          {/* "/gigs/new"). */}
          <Link to="/gigs/new" className="btn btn-primary">
            <i className="bi bi-calendar-plus-fill" aria-hidden="true" /> Add Gig
          </Link>
        </div>
      </div>

      {/* Trip banners — only rendered when there's a trip to show, since
          "Add Trip" now lives in the header instead of a dashed button
          at the end of this list. */}
      {visibleTrips.length > 0 && (
        <div className="cal-trip-section">
          {visibleTrips.map(trip => (
            <TripBanner
              key={trip.id}
              trip={trip}
              isActive={getActiveTripForDate(todayStr)?.id === trip.id}
              onEdit={openEditModal}
              onDelete={handleDeleteRequest}
            />
          ))}
        </div>
      )}

      {/* Calendar grid */}
      <div className="calendar-container">

        {/* Month navigation header */}
        <div className="calendar-header">
          <button onClick={prevMonth} aria-label="Previous month">
            <i className="bi bi-chevron-left" aria-hidden="true" />
          </button>
          <h2>{MONTH_NAMES[currentDate.getMonth()]} {currentDate.getFullYear()}</h2>
          <button onClick={nextMonth} aria-label="Next month">
            <i className="bi bi-chevron-right" aria-hidden="true" />
          </button>
        </div>

        <div className="calendar-grid">

          {/* Day-of-week column headers */}
          {DAY_NAMES.map(day => (
            <div key={day} className="calendar-day-header" aria-label={day}>{day}</div>
          ))}

          {/* Date cells */}
          {getDaysInMonth().map((day, index) => {
            const dayGigs    = getGigsForDay(day)
            const hasGigs    = dayGigs.length > 0
            const isTodayDay = isToday(day)
            const travelInfo = getTravelDayInfo(day)
            const travelKind = travelInfo?.kind || null
            const isTripDay  = !!travelInfo

            // BEM modifier maps trip kind to the colour variant in index.css
            const tripDayCls =
              !isTripDay                 ? '' :
              travelKind === 'check_in'  ? ' calendar-day--trip-depart' :
              travelKind === 'check_out' ? ' calendar-day--trip-return'  :
              travelKind === 'stay'      ? ' calendar-day--trip-stay'    : ''

            // Primary icon = hot pink (check-in / check-out)
            // Secondary icon = muted (stay days)
            const iconCls = travelKind === 'stay'
              ? 'cal-trip-cell-icon cal-trip-cell-icon--secondary'
              : 'cal-trip-cell-icon cal-trip-cell-icon--primary'

            // Descriptive title for keyboard and screen-reader users
            const cellTitle =
              hasGigs && isTripDay
                ? `${dayGigs.length} gig${dayGigs.length > 1 ? 's' : ''} + trip day`
                : hasGigs
                ? `${dayGigs.length} gig${dayGigs.length > 1 ? 's' : ''}`
                : isTripDay
                ? travelInfo.trip.destination_label || 'Trip'
                : undefined

            return (
              <div
                key={index}
                className={[
                  'calendar-day',
                  day         ? ''         : 'empty',
                  hasGigs     ? 'has-gigs' : '',
                  isTodayDay  ? 'today'    : '',
                  isTripDay   ? 'trip-day' : '',
                  tripDayCls,
                  (hasGigs || isTripDay) ? 'cal-day--clickable' : '',
                ].filter(Boolean).join(' ')}
                onClick={() => handleDayClick(day)}
                // Only interactive cells need a button role for accessibility
                role={(hasGigs || isTripDay) ? 'button' : undefined}
                tabIndex={(hasGigs || isTripDay) ? 0 : undefined}
                onKeyDown={e => {
                  if ((hasGigs || isTripDay) && (e.key === 'Enter' || e.key === ' ')) {
                    e.preventDefault()
                    handleDayClick(day)
                  }
                }}
                aria-label={cellTitle ? `${day} — ${cellTitle}` : day ? String(day) : undefined}
                title={cellTitle}
              >
                {day && (
                  <>
                    <span className="day-number">{day}</span>

                    {/* Suitcase icon shown on all trip days */}
                    {isTripDay && (
                      <div className="cal-trip-icon-row">
                        <span
                          className={iconCls}
                          aria-hidden="true"
                        >
                          <i className="bi bi-suitcase-fill" />
                        </span>
                      </div>
                    )}

                    {/* Gig indicator badges */}
                    {hasGigs && (
                      <div className="gig-indicators">
                        {dayGigs.map(gig => (
                          <span
                            key={gig.id}
                            className="gig-indicator"
                          >
                            <i className="bi bi-star-fill" aria-hidden="true" />
                            {' '}{gig.name || gig.venue_name}
                          </span>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            )
          })}
        </div>

        {/* Calendar legend */}
        <div className="cal-legend">
          <span className="cal-legend-item">
            <i
              className="bi bi-suitcase-fill cal-legend-swatch-icon cal-legend-swatch-icon--stay"
              aria-hidden="true"
            />
            On trip (stay)
          </span>
          <span className="cal-legend-item">
            <i
              className="bi bi-suitcase-fill cal-legend-swatch-icon cal-legend-swatch-icon--travel"
              aria-hidden="true"
            />
            Trip Start / End
          </span>
          <span className="cal-legend-item">
            <span className="cal-legend-swatch cal-legend-swatch--gig" aria-hidden="true" />
            Gig
          </span>
          <span className="cal-legend-item">
            <span className="cal-legend-swatch cal-legend-swatch--today" aria-hidden="true" />
            Today
          </span>
        </div>
      </div>


      {/* Modals */}

      <AddTripModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSave={handleSaveTrip}
        initialData={editingTrip}
      />

      {/* DPP-styled delete confirmation — replaces window.confirm */}
      {deletingTrip && (
        <DeleteConfirmModal
          tripLabel={deletingTrip.destination_label}
          onConfirm={handleDeleteConfirm}
          onCancel={() => setDeletingTrip(null)}
        />
      )}

      {selectedDay && (
        <DayGigModal
          dateStr={selectedDay.dateStr}
          gigs={selectedDay.gigs}
          homeLat={homeLat}
          homeLng={homeLng}
          homeAddress={geocodedLabel || homeAddress}
          prepMinutes={prepMinutes}
          musicTracks={musicTracks}
          onClose={() => setSelectedDay(null)}
        />
      )}

      {selectedTripDay && (
        <TripDayModal
          dateStr={selectedTripDay.dateStr}
          travelInfo={selectedTripDay.travelInfo}
          gigs={selectedTripDay.gigs}
          homeLat={homeLat}
          homeLng={homeLng}
          homeAddress={geocodedLabel || homeAddress}
          prepMinutes={prepMinutes}
          musicTracks={musicTracks}
          onClose={() => setSelectedTripDay(null)}
        />
      )}
    </div>
  )
}

export default Calendar